import { useEffect } from 'react';

/**
 * Hook para manejar el cierre de paneles con la tecla Escape
 *
 * @param onClose - Función a ejecutar cuando se presiona Escape
 * @param enabled - Si es false, el hook no hace nada (útil para deshabilitar condicionalmente)
 *
 * Comportamiento:
 * - Solo cierra si no hay un input, textarea o contenteditable con foco
 * - Respeta el comportamiento estándar de Escape (cierra primero dropdowns/modales)
 * - No interfiere con otros usos de Escape en la aplicación
 */
export const useEscapeKey = (
  onClose: (() => void) | undefined,
  enabled: boolean = true
) => {
  useEffect(() => {
    if (!enabled || !onClose) return;

    const handleEscape = (e: KeyboardEvent) => {
      // Solo procesar si es la tecla Escape
      if (e.key !== 'Escape') return;

      // Verificar si hay un input, textarea o elemento contenteditable con foco
      const activeElement = document.activeElement;
      if (activeElement) {
        const tagName = activeElement.tagName.toLowerCase();
        const isInput = tagName === 'input' || tagName === 'textarea';
        const isContentEditable = activeElement.getAttribute('contenteditable') === 'true';

        // Si hay un input/textarea/contenteditable con foco, no cerrar el panel
        // Esto permite que el usuario pueda usar Escape para cancelar ediciones
        if (isInput || isContentEditable) {
          return;
        }
      }

      // Si llegamos aquí, no hay input con foco, podemos cerrar el panel
      onClose();
    };

    window.addEventListener('keydown', handleEscape);
    return () => {
      window.removeEventListener('keydown', handleEscape);
    };
  }, [onClose, enabled]);
};




